import { FC, memo, useEffect, useState } from 'react';
import { parseJson } from '@medicusunion/front-utils';
import { Button, Form, Input, InputNumber, message } from 'antd';
import { InvoiceKeys, InvoiceTypes } from 'constants/invoice';
import { useTranslation } from 'react-i18next';
import { useAppointmentDetails } from 'providers/AppointmentDetails';
import {
  InvoiceTemplate,
  createInvoice,
  fetchInvoiceTemplate
} from 'services/api/appointments';

import { Loading } from 'components/ui';
import { showRestError } from 'utils/showErrorMessage';

interface IProps {
  onClose(): void;
  appointmentId: string;
}

const InvoiceTemplateFields: FC<IProps> = ({ onClose, appointmentId }) => {
  const { t } = useTranslation();
  const { refresh } = useAppointmentDetails();

  const [template, setTemplate] = useState<InvoiceTemplate | null>(null);
  const [isFetching, setIsFetching] = useState(true);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    fetchInvoiceTemplate()
      .then(setTemplate)
      .catch(() => setTemplate(null))
      .finally(() => setIsFetching(false));
  }, []);

  const [form] = Form.useForm();

  const keys = template?.keys || [];

  const onFinish = (values: Record<string, string>) => {
    // legacy AppointmentInvoiceInput fields: [{key_id, label, value}]
    const fields = Object.entries(values).reduce<
      { key_id: string; label: string; value: string }[]
    >((acc, [keyId, value]) => {
      if (value) {
        const key = keys.find(item => `${item?.id}` === keyId);

        if (key) {
          return [
            ...acc,
            { key_id: keyId, label: key.label, value: `${value}` }
          ];
        }
      }

      return acc;
    }, []);

    setIsCreating(true);
    // per-appointment invoice → POST /invoice (NOT /invoice/values)
    createInvoice({
      appointment_id: appointmentId,
      type: InvoiceTypes.template,
      fields
    })
      .then(() => {
        message.success(t('messages.successfully_created'));
        refresh();
        onClose();
      })
      .catch(err => showRestError(err))
      .finally(() => setIsCreating(false));
  };

  const renderField = (type?: number) => {
    switch (type) {
      case InvoiceKeys.text:
        return <Input />;
      case InvoiceKeys.textarea:
        return <Input.TextArea />;
      case InvoiceKeys.number:
        return <InputNumber style={{ width: '100%' }} min={0} />;
      default:
        return null;
    }
  };

  if (isFetching) {
    return <Loading type="static" />;
  }

  return (
    <Form
      disabled={isCreating}
      form={form}
      layout="vertical"
      size="large"
      onFinish={onFinish}
    >
      {keys.map(item => (
        <Form.Item
          key={item?.id}
          label={parseJson(item?.label)?.en}
          name={item?.id}
          rules={[{ required: !!item?.required }]}
        >
          {renderField(item?.type)}
        </Form.Item>
      ))}
      <Form.Item>
        <Button onClick={form.submit} type="primary" loading={isCreating} block>
          {t('common.submit')}
        </Button>
      </Form.Item>
    </Form>
  );
};

export default memo(InvoiceTemplateFields);
